import { useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { ENERGY_LABELS, MOOD_LABELS } from '../store/catalogo';
import type { MoodLog } from '../store/db';
import { recordMood } from '../store/mutations';
import { Card, Deslizador } from '../ui/primitivas';

/**
 * ÁNIMO Y ENERGÍA, en dos arrastres.
 *
 * Cinco niveles, sin caras. Las caritas obligan a traducir un estado a un
 * emoticono, y la mitad de la gente no se reconoce en ninguno.
 *
 * Eran cinco botones sueltos, cada uno con una rayita un poco más alta, y no se
 * leían como una escala: se leían como cinco cosas distintas que por alguna
 * razón crecían. Ahora es un deslizador con sus cinco marcas, que es la forma
 * que todo el mundo ya sabe usar para «de poco a mucho» —el volumen, el brillo—
 * y que además se puede arrastrar con el pulgar sin apuntar a nada.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Y POR ESO SE CONFIRMA. El diseño de botones guardaba solo con que las dos
 * estuvieran puestas, sin preguntar, y al cambiar el control ese automatismo
 * dejó de funcionar: sueltas el pulgar sobre un valor que ya estaba puesto, la
 * tarjeta se da por contestada y se cierra sola con el dedo todavía encima.
 *
 * No es solo que quede feo. Un botón es un compromiso —lo pulsas y ya está—;
 * un deslizador es algo que se tantea: lo mueves, lo miras, lo corriges. Un
 * control que se tantea no puede confirmar solo, porque la mitad de los
 * movimientos no son decisiones. Guardar al soltar convierte cada tanteo en
 * una respuesta.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Vale 3 puntos y van a `general`: registrar cómo estás no es un logro de un
 * área, es información sobre ti. Sirve para las correlaciones de Progreso
 * —ánimo × hábitos, foco × ánimo— que se desbloquean más adelante.
 */
export function AnimoRapido({
  localDate,
  tz,
  registrado,
  alCambiar,
}: {
  localDate: string;
  tz: string;
  /** `undefined` = todavía leyendo IndexedDB. `null` = hoy no hay nada. */
  registrado: MoodLog | null | undefined;
  /**
   * Modo «guarda otro»: dentro del ritual quien guarda es el diálogo, al pasar de
   * paso o cerrar, así que aquí no hay Guardar ni Cancelar ni resumen: solo los
   * deslizadores, y cada movimiento se cuenta al padre. Sin esto, Hoy es igual.
   */
  alCambiar?: ((borrador: { mood: number | null; energy: number | null }) => void) | undefined;
}): ReactElement | null {
  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * EL BORRADOR NO ES UNA COPIA DE LO GUARDADO: ES LO QUE HA TOCADO EL USUARIO.
   *
   * Antes esto era `useState(registrado?.mood ?? null)`, y ese `useState` corre
   * en el PRIMER render —cuando la consulta a IndexedDB todavía no ha vuelto y
   * `registrado` es nulo—. El valor guardado llegaba un instante después y ya no
   * había quien lo metiera en el estado, porque `useState` solo mira su
   * argumento una vez.
   *
   * El resultado: registrabas cómo estabas, recargabas la app, y la tarjeta te
   * lo volvía a preguntar como si no hubieras contestado. Lo guardado estaba ahí
   * —comprobado en la base, `mood 4, energy 2`—, pero la pantalla lo negaba; y
   * si contestabas otra vez, lo pisabas.
   *
   * Es la misma forma que el calendario con veintisiete «0/1» de antes de que el
   * hábito existiera, que el rewind diciendo «paró en octubre» en septiembre y
   * que el «+93» al abrir la app: **una pantalla afirmando algo a partir de un
   * hueco en sus propios datos**. Aquí el hueco es el instante entre montar y
   * leer, y lo que afirma es que no has contestado.
   *
   * La solución es la de siempre: no copiar, derivar. Lo guardado manda mientras
   * nadie lo toque; el borrador solo existe para lo que el usuario acaba de
   * mover, y por eso no puede nacer con una copia de nada.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const [borrador, setBorrador] = useState<{ mood: number | null; energy: number | null }>({
    mood: null,
    energy: null,
  });
  // Un deslizador invita a reajustar, y hasta ahora lo registrado no se podía
  // tocar: la tarjeta pasaba a resumen y ahí se quedaba hasta el día siguiente.
  const [ajustando, setAjustando] = useState(false);

  // Todavía leyendo. No se pinta nada: enseñar «¿cómo estás?» durante un
  // instante a quien ya lo dijo es exactamente el fallo que esto arregla.
  if (registrado === undefined) return null;

  const mood = borrador.mood ?? registrado?.mood ?? null;
  const energy = borrador.energy ?? registrado?.energy ?? null;

  // Mover un deslizador solo mueve el borrador. Nada se escribe hasta que lo
  // dices tú.
  const elegir = (campo: 'mood' | 'energy', valor: number): void => {
    const siguiente = { mood, energy, [campo]: valor };
    setBorrador(siguiente);
    alCambiar?.(siguiente);
  };

  const guardar = (): void => {
    // Una sola no es un registro: es media pregunta contestada, y guardarla
    // obligaría a inventarse la otra mitad.
    if (mood === null || energy === null) return;
    void recordMood({ localDate, mood, energy, tz });
    setAjustando(false);
  };

  const cancelar = (): void => {
    setBorrador({ mood: null, energy: null });
    setAjustando(false);
  };

  const cuadra = registrado !== null && mood === registrado.mood && energy === registrado.energy;
  const falta = mood === null ? 'el ánimo' : energy === null ? 'la energía' : null;

  if (cuadra && !ajustando && alCambiar === undefined) {
    return (
      <Card>
        <div className="row row--between">
          <div>
            <p className="t-caption t-dim">Hoy te sientes</p>
            <p className="t-title-3">
              {MOOD_LABELS[registrado.mood - 1]} · {ENERGY_LABELS[registrado.energy - 1]}
            </p>
          </div>
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => setAjustando(true)}
            >
              Ajustar
            </button>
            <Link to="/diario" className="btn btn--sm btn--ghost">
              Diario
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card title="¿Cómo estás?">
      <div className="stack stack--loose">
        <Deslizador
          label="Ánimo"
          labels={MOOD_LABELS}
          value={mood}
          onChange={(v) => elegir('mood', v)}
        />
        <Deslizador
          label="Energía"
          labels={ENERGY_LABELS}
          value={energy}
          onChange={(v) => elegir('energy', v)}
        />

        {alCambiar !== undefined ? null : (
          <div className="row row--between">
            {/* La pista solo aparece cuando has empezado y te falta una. Antes de
              tocar nada no hay nada que reprochar, y con las dos puestas el
              botón ya no necesita explicación. */}
            {registrado !== null ? (
              <button type="button" className="btn btn--bare" onClick={cancelar}>
                Cancelar
              </button>
            ) : (
              <span className="t-sm t-dim">
                {mood === null && energy === null ? '' : falta ? `Falta ${falta}` : ''}
              </span>
            )}
            <button
              type="button"
              className="btn btn--primary"
              disabled={falta !== null}
              onClick={guardar}
            >
              Guardar
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}
